import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import type { LoadState } from '../loadState';

export interface GameRow {
  id: string;
  opponent: string;
  date: string;
  home_away: 'HOME' | 'AWAY' | 'NEUTRAL';
  competition: string | null;
  kickoff_time: string | null;
  venue: string | null;
  result: string | null;
  formation_id: string | null;
  lineup: unknown;
  subs: unknown;
  opponent_notes_pre: string | null;
  opponent_notes_post: string | null;
  match_report: string | null;
  pregame_brief: string | null;
}

interface TeamGamesResult {
  rows: GameRow[];
  state: LoadState;
  errorMessage?: string;
  refresh: () => void;
}

const SELECT = [
  'id', 'opponent', 'date', 'home_away', 'competition', 'kickoff_time', 'venue',
  'result', 'formation_id', 'lineup', 'subs',
  'opponent_notes_pre', 'opponent_notes_post', 'match_report', 'pregame_brief',
].join(', ');

/** Live games for a team, ordered by date. No offline fallback —
 *  games are coach-authored data; missing them is the empty state. */
export function useTeamGames(
  teamId: string | null | undefined,
  teamLoading: boolean,
): TeamGamesResult {
  const [rows, setRows] = useState<GameRow[]>([]);
  const [state, setState] = useState<LoadState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (teamLoading) {
      setState('loading');
      return;
    }
    if (!teamId) {
      setRows([]);
      setState('empty');
      return;
    }
    let cancelled = false;
    setState('loading');
    setErrorMessage(undefined);
    void (async () => {
      const { data, error } = await supabase
        .from('games')
        .select(SELECT)
        .eq('team_id', teamId)
        .is('deleted_at', null)
        .order('date', { ascending: false })
        .limit(40);
      if (cancelled) return;
      if (error) {
        setErrorMessage(error.message);
        setState('error');
        return;
      }
      const list = (data ?? []) as unknown as GameRow[];
      setRows(list);
      setState(list.length > 0 ? 'ok' : 'empty');
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId, teamLoading, refreshTick]);

  return { rows, state, errorMessage, refresh: () => setRefreshTick((n) => n + 1) };
}

/** Single-game read for the GameDay page. */
export function useGame(
  gameId: string | null | undefined,
): { row: GameRow | null; state: LoadState; errorMessage?: string; refresh: () => void } {
  const [row, setRow] = useState<GameRow | null>(null);
  const [state, setState] = useState<LoadState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!gameId) {
      setRow(null);
      setState('empty');
      return;
    }
    let cancelled = false;
    setState('loading');
    setErrorMessage(undefined);
    void (async () => {
      const { data, error } = await supabase
        .from('games')
        .select(SELECT)
        .eq('id', gameId)
        .is('deleted_at', null)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        setErrorMessage(error.message);
        setState('error');
        return;
      }
      setRow((data as unknown as GameRow) ?? null);
      setState(data ? 'ok' : 'empty');
    })();
    return () => {
      cancelled = true;
    };
  }, [gameId, refreshTick]);

  return { row, state, errorMessage, refresh: () => setRefreshTick((n) => n + 1) };
}
